import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { AulaEvento } from '@eventi/tipi.ts';
import { Input } from '../ui/input';
import { cn } from '../../lib/cn';
import { leggiPosti } from '../../lib/eventi/impostazioni';

type Props = {
  aula: AulaEvento;
  onSalva: (codiceAula: string, posti: number | null) => Promise<void>;
};

/** 'errore' = numero scritto male; 'fallito' = il database non l'ha salvato (l'avviso lo mostra la pagina). */
type Stato = 'fermo' | 'salvo' | 'salvato' | 'errore' | 'fallito';

/** I posti di un'aula: si salva da solo quando si esce dal campo. Vuoto = senza limite. */
export function CampoPosti({ aula, onSalva }: Props) {
  const [testo, setTesto] = useState(aula.posti === null ? '' : String(aula.posti));
  const [stato, setStato] = useState<Stato>('fermo');

  useEffect(() => setTesto(aula.posti === null ? '' : String(aula.posti)), [aula.posti]);

  const salva = async () => {
    const esito = leggiPosti(testo);
    if (!esito.ok) {
      setStato('errore');
      return;
    }
    if (esito.posti === aula.posti) {
      setStato('fermo');
      return;
    }
    setStato('salvo');
    try {
      await onSalva(aula.codice, esito.posti);
      setStato('salvato');
    } catch {
      setStato('fallito');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <Input
          inputMode="numeric"
          aria-label={`Posti in ${aula.nome}`}
          title="Vuoto = senza limite"
          value={testo}
          placeholder="∞"
          onChange={(e) => {
            setTesto(e.target.value);
            setStato('fermo');
          }}
          onBlur={() => void salva()}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          className={cn(
            'h-8 w-16 px-2 text-sm tabular-nums bg-white',
            (stato === 'errore' || stato === 'fallito') && 'border-red-400 focus-visible:ring-red-400',
          )}
        />
        <span className="text-[11px] text-neutral-500">posti</span>
        {stato === 'salvo' && <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />}
        {stato === 'salvato' && <Check className="w-3.5 h-3.5 text-green-600" aria-label="Salvato" />}
      </div>
      {stato === 'errore' && <span className="block text-[11px] text-red-600 mt-1">Un numero intero, o vuoto</span>}
      {stato === 'fallito' && <span className="block text-[11px] text-red-600 mt-1">Non salvato: riprova</span>}
    </div>
  );
}
